import { useEffect, useState } from "react"
import Grid from "@mui/material/Grid"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"
import MDNumberInput from "../MDNumberInput"
import MDTypography from "components/MDTypography"
import SectionCard from "../SectionCard"
import { useCreatePurchaseGrnMutation } from "shared/redux/endpoints/procurementGrn"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { getApiErrorMessage } from "../../orderSubmitFeedback"

const remainingOf = (l) => Math.max((Number(l.orderedQty) || 0) - (Number(l.receivedQty) || 0), 0)

// Procurement enters one delivery batch at a time (POST /purchases/{id}/grn).
// Quantities are for this batch only; the PO's running totals come back in
// receiptProgress, so each row defaults to what is still outstanding.
const GoodsReceiptPanel = ({ purchaseId, progressLines = [], readOnly = false }) => {
  const [createGrn, { isLoading: saving }] = useCreatePurchaseGrnMutation()
  const [qty, setQty] = useState({})
  const [note, setNote] = useState("")

  useEffect(() => {
    setQty(Object.fromEntries(progressLines.map((l) => [l.orderRequestLineId, String(remainingOf(l))])))
  }, [progressLines])

  const batch = progressLines
    .map((l) => ({ orderRequestLineId: l.orderRequestLineId, receivedQty: Number(qty[l.orderRequestLineId]) || 0 }))
    .filter((l) => l.receivedQty > 0)

  const handleSave = async () => {
    try {
      await createGrn({ purchaseId, note: note.trim(), lines: batch }).unwrap()
      setNote("")
      showToastSuccess("Đã lưu đợt nhận hàng.")
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Không thể lưu đợt nhận hàng. Vui lòng thử lại."))
    }
  }

  if (!progressLines.length || readOnly) return null

  return (
    <SectionCard icon="inventory_2" title="Nhập nhận hàng" subtitle="Nhập SL của đợt giao này — không cộng dồn">
      <MDBox display="flex" flexDirection="column" gap={1.5}>
        {progressLines.map((l) => (
          <MDBox key={l.orderRequestLineId} p={2} borderRadius="lg" bgColor="grey-100">
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={7}>
                <MDTypography variant="button" fontWeight="medium" display="block">
                  {l.itemName || `Dòng ${l.orderRequestLineId}`}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {[l.orderCode, `Đã nhận ${l.receivedQty ?? 0} / ${l.orderedQty ?? 0}`].filter(Boolean).join(" · ")}
                </MDTypography>
              </Grid>
              <Grid item xs={12} md={5}>
                <MDNumberInput
                  fullWidth
                  labelType="non-sticky"
                  labelText="SL nhận đợt này"
                  value={qty[l.orderRequestLineId] ?? ""}
                  onChange={(v) => setQty((p) => ({ ...p, [l.orderRequestLineId]: v }))}
                />
              </Grid>
            </Grid>
          </MDBox>
        ))}
        <MDInput
          fullWidth
          labelType="non-sticky"
          labelText="Ghi chú"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </MDBox>
      <MDBox mt={2}>
        <MDButton variant="gradient" color="info" disabled={saving || batch.length === 0} onClick={handleSave}>
          {saving ? "Đang lưu…" : "Lưu đợt nhận hàng"}
        </MDButton>
      </MDBox>
    </SectionCard>
  )
}

export default GoodsReceiptPanel
